import React, {useCallback, useEffect, useState} from 'react';
import {initializeApp} from 'firebase/app';
import {getAuth, signOut} from "firebase/auth";
import {doc, getFirestore, setDoc} from 'firebase/firestore';
import {useAuthState} from "react-firebase-hooks/auth";
import {useDocument} from 'react-firebase-hooks/firestore';
import axios from 'axios';

// Config variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};
const LS_USER = 'user';
const LS_PROFILE = 'profile';
const LS_ACCESS_TOKEN_GOOGLE = 'g__access_token';
const initProfile = {
  spreadsheetId: null,
  sheetIdExpenses: null,
  sheetIdExchanges: null,
  sheetIdCategories: null,
};

// const prefix = import.meta.env.VITE_FIREBASE_DATABASE_PREFIX;

const FirebaseContext = React.createContext(null);
export default FirebaseContext;

export function FirebaseProvider({children}) {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem(LS_USER)));
  const [accessTokenGoogle, setAccessTokenGoogle] = useState(localStorage.getItem(LS_ACCESS_TOKEN_GOOGLE));
  const [profile, setProfile] = useState(JSON.parse(localStorage.getItem(LS_PROFILE)) || initProfile);
  const app = initializeApp(firebaseConfig);

  // Initialize Firebase Authentication and get a reference to the service
  const auth = getAuth(app);

  // Get current authenticated user
  const [, userLoading, userError] = useAuthState(auth, {
    onUserChanged: (user) => {
      // store user in local storage for quicker access on initial load
      if (user) {
        setUser(user);
        localStorage.setItem(LS_USER, JSON.stringify(user));
      } else {
        setUser(null);
        localStorage.removeItem(LS_USER);
      }
    }
  });

  // todo: remove profile loading
  // Get current user's profile from firestore
  const prefix = import.meta.env.VITE_FIREBASE_DATABASE_PREFIX;
  const profileDocRef = doc(getFirestore(app), prefix + 'users', user ? user.uid : 'fakeid');
  const [snapshot, profileLoading, profileError] = useDocument(profileDocRef);

  useEffect(() => {
    if (!profileLoading) {
      if (snapshot && snapshot.exists() && snapshot.data()) {
        setProfile(snapshot.data());
        localStorage.setItem(LS_PROFILE, JSON.stringify(snapshot.data()));
      } else {
        setProfile(initProfile);
        localStorage.removeItem(LS_PROFILE);
      }
    }
  }, [snapshot, profileLoading]);

  const context = {
    app,
    auth,
    user,
    userLoading,
    userError,
    accessTokenGoogle,
    profile: snapshot && snapshot.exists() ? snapshot.data() : profile,
    profileLoading,
    profileError,
    signOut: () => {
      signOut(auth);
    },
    saveProfile: async (profile) => {
      await setDoc(profileDocRef, profile, { merge: true });
    },
    signInSuccess: (authResult) => {
      // store the access token for Google
      // This assumes Google login. Might fail with email login. Oops. Problem for future me
      const { accessToken } = authResult.credential;
      if (accessToken) {
        setAccessTokenGoogle(accessToken);
        localStorage.setItem(LS_ACCESS_TOKEN_GOOGLE, accessToken);
      } else {
        setAccessTokenGoogle(null);
        localStorage.removeItem(LS_ACCESS_TOKEN_GOOGLE);
      }
      return true;
    },
    firebaseRequest: useCallback(async (axiosConfig) => {
      let response;
      try {
        const token = await auth.currentUser.getIdToken();
        // const token = await user.getIdToken();
        // console.log('token', token);
        const config = {
          ...axiosConfig,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        response = await axios.request(config);
        return response.data;
      } catch (ex) {
        console.error(ex);
        if (ex.response) {
          return ex.response.data;
        }
        return null;
      }
    }, [auth.currentUser]),
    getIdToken: useCallback(async () => {
      return await auth.currentUser.getIdToken();
    }, [auth.currentUser]),
  };
  return <FirebaseContext.Provider value={context}>
    {children}
  </FirebaseContext.Provider>
}
